import React from 'react';
import {Collapse, Row} from 'antd';
import {isEmpty} from 'underscore';
import {DTFullFormat} from '../../../../utils/StaticVariable';
import {DownloadInfoItem} from '../../../../types/analytics.d';
import {getCategoryPostfix} from '../../../../utils/PointHelper';
import styles from '../Style/DatasetInfo.module.scss';
import {useIsGlobalHDDtu, useIsTnw} from '../../../../utils/hooks';
import DownloadDataModal from '../../../common/Modals/Download/DownloadDataModal';
import {useMarkerStore} from "../../../../store/markerStore";
import useHomeStore from "../../../../store/homeStore";
import useDatasetStore from "../../../../store/datasetStore";
import useAuthStore from "../../../../store/authStore";
import useSearchStore from "../../../../store/searchStore";
import {Label} from "../../../common/Label/Label";
import {useSelectedDataset} from "../../../layout/Header/components/SearchBar";
import {useLocationAvailable} from "../../../../hooks/useLocationAvailable";
import {useDatasetCart} from "../hooks/useDatasetCart";
import {useDatasetDownload} from "../hooks/useDatasetDownload";
import {useDatasetMetadata} from "../hooks/useDatasetMetadata";
import {useDatasetParameters} from "../hooks/useDatasetParameter";
import {useDatasetPrice} from "../hooks/useDatasetPrice";
import {useDatasetChange} from "../hooks/useDatasetChange";
import {useDatasetSpectraFeature} from "../hooks/useDatasetSpectraFeature";
import {DatasetChangeWarningModal} from "./Modal/DatasetWarning/DatasetChangeWarningModal";
import {TNSWarningModal} from "./Modal/TNSWarning/TNSWarningModal";
import {DownloadOptionsModal} from "./Modal/DownloadOptions/DownloadOptionsModal";
import {ModelDataSelector} from "./Selector/ModelSelector/ModelDataSelector";
import {DateRangeSelector} from "./Selector/DateRangeSelector/DateRangeSelector";
import {LocationInfoDisplay} from "./Display/LocationInfoDisplay/LocationInfoDisplay";
import {PriceSection} from "./Display/Price/PriceSection";

export default function DatasetInfo(props: {
  item: DownloadInfoItem; //Best datasets of one categotry (Waves, Ocean, Atmosphere)
  coords: number[];
  onMeshControl: (value: string) => any;
  onRemoveProductFromCart: (pointName: string, category: string) => any;
  onAddProductToCart: (
    item: DownloadInfoItem,
    pointName: string,
    category: string,
    coordinates: number[]
  ) => Promise<boolean>;
  downloadItems: DownloadInfoItem[];
  label: string;
  disableDownload: boolean;
  setDisableDownload: React.Dispatch<React.SetStateAction<boolean>>;
  setDatasetIsOfflineWarningVisible: (value: boolean) => void
  icon?: JSX.Element;
  namePanel: string,
}) {
  const setShowSpectraModal = useHomeStore(state => state.setShowSpectraModal);
  const timeStepInterval = useHomeStore(state => state.timeStepInterval)
  const setTimeStepInterval = useHomeStore(state => state.setTimeStepInterval)
  const datasetBriefs = useDatasetStore(state => state.datasetBriefs)
  const userGroupPermissions = useAuthStore(state => state.userGroupPermissions);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const setSearchId = useSearchStore(state => state.setSearchId)

  const { onChange }= useSelectedDataset((id) => setSearchId(id))

  const isTnw = useIsTnw(props.item.datasetId);
  const isDTU = useIsGlobalHDDtu(props.item.datasetId)

  const point = useMarkerStore(state => state.activeMarker)

  const {
    price
  } = useDatasetPrice(props.item.datasetId, props.item.dateRange?.[0].format(DTFullFormat), props.item.dateRange?.[1].format(DTFullFormat))

  const {
    cartLoading,
    addedToCart,
    handleAddToCart,
    handleRemoveFromCart
  } = useDatasetCart(props.item, props.coords, props.label, props.namePanel, price, isDTU, props.onAddProductToCart, props.onRemoveProductFromCart)

  const {
    downloadValues,
    downloadOptions,
    downloadType,
    isDownloadModalVisible,
    showWarn,
    checkPolicy,
    isDownloadSpectraModalVisible,
    showCancelDownloadButtonParameters,
    showCancelDownloadButton2DSpectre,
    handleChangeDownloadOptions,
    handlePolicyChange,
    handleDownloadModalCancel,
    setIsDownloadSpectraModalVisible,
    setShowWarn,
    handleDownload,
    handleCancelDownload,
    handleStartDownloadProcess,
    resetDownloadProcess,
  } = useDatasetDownload(props.item, props.coords, props.label, props.namePanel, price, props.setDatasetIsOfflineWarningVisible)

  const {
    disabledDate,
  } = useDatasetParameters(props.namePanel, props.coords, props.item.datasetId)

  const {
    onDatasetChange,
    handleChangeDatasetWarningOk,
    handleChangeDatasetWarningCancel,
    onDateChange,
    handleChangePanelCategory,
    isChangeDatasetWarningVisible
  } = useDatasetChange(props.item, props.namePanel ,props.downloadItems, props.onMeshControl)

  const {
    onShowMetadata,
    hasDatasets,
    datasetIsOffline,
    showDownloadButton,
    isTNSDatasetWarningVisible,
    setIsTNSDatasetWarningVisible
  } = useDatasetMetadata(
    props.namePanel,
    props.coords,
    props.item,
    onDateChange,
    handleChangePanelCategory,
  );

  const {
    show2DSpectra
  } = useDatasetSpectraFeature(props.coords, props.item.datasetId, props.label)

  const isLocationAvailable = useLocationAvailable(props.namePanel, props.item.datasetId, datasetBriefs, datasetIsOffline)

  return (
    <>
      <DatasetChangeWarningModal
        visible={isChangeDatasetWarningVisible}
        onOk={handleChangeDatasetWarningOk}
        onCancel={handleChangeDatasetWarningCancel}
      />

      <DownloadDataModal
        isVisible={isDownloadSpectraModalVisible}
        setIsVisible={setIsDownloadSpectraModalVisible}
      />

      <TNSWarningModal
        visible={isTNSDatasetWarningVisible}
        onClose={() => setIsTNSDatasetWarningVisible(false)}
      />

      <DownloadOptionsModal
        visible={isDownloadModalVisible}
        title="Download"
        options={downloadOptions}
        values={downloadValues}
        showWarn={showWarn}
        checkPolicy={checkPolicy}
        downloadType={downloadType}
        onCancel={handleDownloadModalCancel}
        onOk={() => {
          if (checkPolicy && downloadType) {
            handleDownload(downloadType, datasetIsOffline);
            resetDownloadProcess();
          } else {
            setShowWarn(true);
          }
        }}
        onChange={handleChangeDownloadOptions}
        onPolicyChange={handlePolicyChange}
        disableDownload={props.disableDownload}
        setDisableDownload={props.setDisableDownload}
        setShowWarn={setShowWarn}
      />

      <Collapse
        accordion
        activeKey={point?.activeDatasetKey ?? ''}
        onChange={handleChangePanelCategory}
        className={`${styles.customCollapse} ${point && point.activeDatasetKey === props.namePanel ? styles.activeDataset : ''}`}
      >
        <Collapse.Panel
          className={styles.datasetItem}
          header={
            <Label
              label={
                <div className={styles.DatasetCategory}>
                  {props.icon}
                  <div className={styles.DatasetCategoryLabel}>
                    <h2>{props.label}</h2>
                    {getCategoryPostfix(props.label)}
                  </div>
                </div>
              }
            />
          }
          key={props.namePanel}
        >
          <Row gutter={16}>
            <ModelDataSelector
              id={`PointPanel-Dataset_Selection_${point?.label}_${props.namePanel}`}
              item={props.item}
              datasetId={props.item.datasetId}
              options={props.item.options}
              onShowMetadata={onShowMetadata}
              onDatasetChange={onDatasetChange}
              isDisabled={addedToCart}
              onChange={onChange}
            />

            <DateRangeSelector
              id={`PointPanel-Date_Selection_${point?.label}_${props.namePanel}`}
              dateRange={props.item.dateRange}
              disabledDate={disabledDate}
              onDateChange={(value) => onDateChange(value, false)}
              isDisabled={isEmpty(props.item.datasetId) || addedToCart}
            />

            <LocationInfoDisplay
              hasDatasets={hasDatasets}
              isLocationAvailable={isLocationAvailable}
              waterDepth={point?.waterDepth?.[props.namePanel]}
              cellSize={point?.cellSize?.[props.namePanel]}
              isWind={props.item.radioKey === 'Wind'}
            />

            <PriceSection
              hasDatasets={hasDatasets}
              price={price}
              isTnw={isTnw}
              namePanel={props.namePanel}
              disableDownload={props.disableDownload}
              isLocationAvailable={isLocationAvailable}

              isDTU={isDTU}
              timeStepInterval={timeStepInterval}
              setTimeStepInterval={setTimeStepInterval}
              datasetId={props.item.datasetId}

              addedToCart={addedToCart}
              showDownloadButton={showDownloadButton}
              showCancelDownloadButtonParameters={showCancelDownloadButtonParameters}
              showCancelDownloadButton2DSpectre={showCancelDownloadButton2DSpectre}

              show2DSpectra={show2DSpectra}

              isAuthenticated={isAuthenticated()}
              userId={point?.id}
              label={point?.label}
              userGroupPermissions={userGroupPermissions}

              handleAddToCart={handleAddToCart}
              handleRemoveFromCart={handleRemoveFromCart}
              handleStartDownloadProcess={handleStartDownloadProcess}
              handleCancelDownload={handleCancelDownload}
              setShowSpectraModal={setShowSpectraModal}

              cartLoading={cartLoading}
            />
          </Row>
        </Collapse.Panel>
      </Collapse>
    </>
  );
}
